/**
 * Poly Hydration — Logging Utility
 *
 * Centralized logging with level-based gating and RUM error emission.
 * - debug/info: only output when DEBUG is active
 * - warn: only output when DEBUG is active
 * - error: always output to console, and emitted to CloudWatch RUM when available
 */

import { DEBUG } from './config';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const PREFIX = '[PolyHydration]';

/**
 * Logs a message at the specified level.
 *
 * - debug, info, warn: gated by DEBUG (build-time or ?hydrationDebug=true)
 * - error: always logged to console, and emitted to RUM as a custom event
 */
export function log(level: LogLevel, message: string, ...args: unknown[]): void {
  if (level === 'error') {
    console.error(PREFIX, message, ...args);
    emitErrorToRum(message, args);
    return;
  }

  if (!DEBUG) return;

  switch (level) {
    case 'debug':
      console.log(PREFIX, message, ...args);
      break;
    case 'info':
      console.log(PREFIX, message, ...args);
      break;
    case 'warn':
      console.warn(PREFIX, message, ...args);
      break;
  }
}

/**
 * Best-effort emission of error-level logs to CloudWatch RUM.
 * No-op if cwr is unavailable. Errors in emission are silently swallowed.
 */
function emitErrorToRum(message: string, args: unknown[]): void {
  if (typeof cwr !== 'function') return;

  try {
    cwr('recordEvent', {
      type: 'poly_hydration_error',
      data: {
        message,
        details: args.length > 0 ? String(args[0]) : undefined,
      },
    });
  } catch {
    // Telemetry errors must never break hydration
  }
}
